import './HallPointsSimulator.scss';

import { Accordion, ButtonGroup, Col, Container, Form, Row, ToggleButton } from 'react-bootstrap';
import { useMemo, useState } from 'react';

import AppInput from '../../../commons/inputs/AppInput';
import FloatingVideoButton from '../../../commons/buttons/FloatingVideoButton';
import MenuTitle from '../../../commons/menu/MenuTitle';
import { formatInReal } from '../../../commons/util/HallAppUtil';

export default function HallPointsSimulator() {
  const [tipoSimulacao, setTipoSimulacao] = useState('A');

  // A
  const [pointsEarned, setPointsEarned] = useState('');
  const [realSpentToEarnPoints, setRealSpentToEarnPoints] = useState('');
  const [redemptionPoints, setRedemptionPoints] = useState('');
  const [benefitValue, setBenefitValue] = useState('');

  // B
  const [pointsPerService, setPointsPerService] = useState('');
  const [pointsToRedeem, setPointsToRedeem] = useState('');
  const [benefitValueB, setBenefitValueB] = useState('');

  // C
  const [percentualPontos, setPercentualPontos] = useState('');
  const [pontosParaResgatarC, setPontosParaResgatarC] = useState('');
  const [valorBeneficioC, setValorBeneficioC] = useState('');

  const radios = [
    { name: '[A] Pontos por valor gasto', value: 'A' },
    { name: '[B] Pontos fixos por serviço', value: 'B' },
    { name: '[C] Proporcional ao valor gasto', value: 'C' },
  ];

  // Helpers
  const parseNum = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    // Permite "10,50" ou "10.50"
    const norm = String(v).replace(/\./g, '').replace(',', '.');
    const n = Number(norm);
    return Number.isFinite(n) ? n : 0;
  };

  const onlyNumber = (setter) => (e) => {
    const val = e.target.value;
    // Aceita dígitos, vírgula, ponto
    if (/^[0-9]*[.,]?[0-9]*$/.test(val) || val === '') {
      setter(val);
    }
  };

  return (
    <Container className="py-4 hallpoints">
      <MenuTitle title="Simulador HallPoints" />
      <p className="text-muted mb-3">
        Simule diferentes modelos de pontuação e resgate para encontrar a configuração ideal do seu programa de fidelidade.
      </p>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="me-2 fw-semibold">Tipo de simulação:</span>
        <ButtonGroup>
          {radios.map((r, idx) => (
            <ToggleButton
              key={idx}
              id={`radio-${r.value}`}
              type="radio"
              variant={tipoSimulacao === r.value ? 'primary' : 'outline-primary'}
              name="radio"
              value={r.value}
              checked={tipoSimulacao === r.value}
              onChange={(e) => setTipoSimulacao(e.currentTarget.value)}
              size="sm"
            >
              {r.name}
            </ToggleButton>
          ))}
        </ButtonGroup>
      </div>

      <div className="mb-2">
        <small className="text-muted">
          Dica: busque um retorno de 5% a 10% para ser atraente sem comprometer sua margem.
        </small>
      </div>

      {/* A */}
      <div className={tipoSimulacao === 'A' ? 'd-block' : 'd-none'}>
        <SimulacaoPorValorGasto
          pointsEarned={pointsEarned}
          realSpentToEarnPoints={realSpentToEarnPoints}
          redemptionPoints={redemptionPoints}
          benefitValue={benefitValue}
          setPointsEarned={onlyNumber(setPointsEarned)}
          setRealSpentToEarnPoints={onlyNumber(setRealSpentToEarnPoints)}
          setRedemptionPoints={onlyNumber(setRedemptionPoints)}
          setBenefitValue={onlyNumber(setBenefitValue)}
          parseNum={parseNum}
        />
      </div>

      {/* B */}
      <div className={tipoSimulacao === 'B' ? 'd-block' : 'd-none'}>
        <SimulacaoPontosFixos
          pointsPerService={pointsPerService}
          pointsToRedeem={pointsToRedeem}
          benefitValue={benefitValueB}
          setPointsPerService={onlyNumber(setPointsPerService)}
          setPointsToRedeem={onlyNumber(setPointsToRedeem)}
          setBenefitValue={onlyNumber(setBenefitValueB)}
          parseNum={parseNum}
        />
      </div>

      {/* C */}
      <div className={tipoSimulacao === 'C' ? 'd-block' : 'd-none'}>
        <SimulacaoProporcional
          percentualPontos={percentualPontos}
          pontosParaResgatar={pontosParaResgatarC}
          valorBeneficio={valorBeneficioC}
          setPercentualPontos={onlyNumber(setPercentualPontos)}
          setPontosParaResgatar={onlyNumber(setPontosParaResgatarC)}
          setValorBeneficio={onlyNumber(setValorBeneficioC)}
          parseNum={parseNum}
        />
      </div>
    </Container>
  );
}

/* Componentes */

function InfoAccordion({ eventKey = '0', title = 'Dicas e exemplo', children }) {
  return (
    <Accordion className="mt-3">
      <Accordion.Item eventKey={eventKey}>
        <Accordion.Header>💡 {title}</Accordion.Header>
        <Accordion.Body>{children}</Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

function SimulacaoPorValorGasto({
  pointsEarned,
  realSpentToEarnPoints,
  redemptionPoints,
  benefitValue,
  setPointsEarned,
  setRealSpentToEarnPoints,
  setRedemptionPoints,
  setBenefitValue,
  parseNum,
}) {
  const pontosGanhos = parseNum(pointsEarned);
  const reaisGastos = parseNum(realSpentToEarnPoints);
  const pontosParaResgatar = parseNum(redemptionPoints);
  const valorBeneficio = parseNum(benefitValue);

  const reaisPorPonto = useMemo(
    () => (pontosGanhos > 0 ? reaisGastos / pontosGanhos : 0),
    [reaisGastos, pontosGanhos]
  );
  const valorNecessario = useMemo(
    () => (reaisPorPonto > 0 ? pontosParaResgatar * reaisPorPonto : 0),
    [pontosParaResgatar, reaisPorPonto]
  );
  const retornoPct = useMemo(
    () => (valorNecessario > 0 ? (valorBeneficio / valorNecessario) * 100 : 0),
    [valorBeneficio, valorNecessario]
  );

  return (
    <>
      <p className="mb-3">Simulação A: o cliente acumula pontos conforme o valor gasto.</p>

      <InfoAccordion title="Dicas para configurar (Simulação A)">
        <ul className="mb-2">
          <li>Defina quantos pontos o cliente ganha a cada valor gasto (ex.: 100 pts a cada R$ 200).</li>
          <li>O sistema acumula os valores até atingir o limite; não precisa gastar de uma vez.</li>
          <li>Busque um retorno entre 5% e 10% ao final do ciclo de pontos.</li>
        </ul>
        <div className="border rounded p-3 bg-light">
          <strong>Exemplo:</strong><br />
          - A cada R$200 gastos → 100 pontos<br />
          - Resgate: 2000 pontos = R$150<br />
          - É preciso gastar R$4000 para resgatar R$150 (retorno ~3,75%)
        </div>
      </InfoAccordion>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <AppInput
            label="Pontos ganhos"
            name="pointsEarned"
            placeholder="Ex.: 100"
            errors={[]}
            value={pointsEarned}
            onChange={setPointsEarned}
          />
        </Col>
        <Col md={6}>
          <AppInput
            label="Reais necessários para ganhar esses pontos (R$)"
            name="realSpentToEarnPoints"
            placeholder="Ex.: 200"
            errors={[]}
            value={realSpentToEarnPoints}
            onChange={setRealSpentToEarnPoints}
          />
        </Col>
        <Col md={6}>
          <AppInput
            label="Pontos necessários para resgatar"
            name="redemptionPoints"
            placeholder="Ex.: 2000"
            errors={[]}
            value={redemptionPoints}
            onChange={setRedemptionPoints}
          />
        </Col>
        <Col md={6}>
          <AppInput
            label="Valor do benefício (R$)"
            name="benefitValue"
            placeholder="Ex.: 150"
            errors={[]}
            value={benefitValue}
            onChange={setBenefitValue}
          />
        </Col>
      </Row>
      <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://www.youtube.com/embed/hYapRxMTAKY"} />

      <div className="mt-4 p-3 border bg-light rounded">
        <strong>Resultado:</strong>
        <p className="mt-2 mb-0 text-muted">
          {valorNecessario > 0 ? (
            <>
              O cliente precisa gastar <strong>{formatInReal(valorNecessario)}</strong> para resgatar{' '}
              <strong>{formatInReal(valorBeneficio)}</strong>.<br />
              Retorno estimado: <strong>{retornoPct.toFixed(1)}%</strong> (
              ~R${(valorBeneficio / (valorNecessario || 1)).toFixed(2)} de volta por R$1,00 gasto).
            </>
          ) : (
            <>Preencha todos os campos para ver a simulação.</>
          )}
        </p>
      </div>
    </>
  );
}

function SimulacaoPontosFixos({
  pointsPerService,
  pointsToRedeem,
  benefitValue,
  setPointsPerService,
  setPointsToRedeem,
  setBenefitValue,
  parseNum,
}) {
  const pontosPorServico = parseNum(pointsPerService);
  const pontosNecessarios = parseNum(pointsToRedeem);
  const valorBeneficio = parseNum(benefitValue);

  const servicosNecessarios = useMemo(
    () => (pontosPorServico > 0 ? Math.ceil(pontosNecessarios / pontosPorServico) : 0),
    [pontosNecessarios, pontosPorServico]
  );

  const retornoPorServico = useMemo(
    () => (servicosNecessarios > 0 ? valorBeneficio / servicosNecessarios : 0),
    [valorBeneficio, servicosNecessarios]
  );

  return (
    <>
      <p className="mb-3">Simulação B: o cliente acumula pontos fixos por serviço realizado.</p>

      <InfoAccordion title="Dicas para configurar (Simulação B)">
        <ul className="mb-2">
          <li>Defina pontos por serviço (ex.: corte 30 pts, escova 20 pts).</li>
          <li>Serviços mais valiosos podem render mais pontos.</li>
          <li>Útil para promover serviços estratégicos e horários ociosos.</li>
        </ul>
        <div className="border rounded p-3 bg-light">
          <strong>Exemplo:</strong><br />
          - Corte: 30 pts, Hidratação: 40 pts, Progressiva: 80 pts<br />
          - Resgate: 200 pts = R$60 → retorno ~6% a 9%
        </div>
      </InfoAccordion>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <AppInput
            label="Pontos por serviço"
            name="pointsPerService"
            placeholder="Ex.: 30"
            errors={[]}
            value={pointsPerService}
            onChange={setPointsPerService}
          />
        </Col>
        <Col md={4}>
          <AppInput
            label="Pontos para resgate"
            name="pointsToRedeem"
            placeholder="Ex.: 200"
            errors={[]}
            value={pointsToRedeem}
            onChange={setPointsToRedeem}
          />
        </Col>
        <Col md={4}>
          <AppInput
            label="Valor do benefício (R$)"
            name="benefitValue"
            placeholder="Ex.: 60"
            errors={[]}
            value={benefitValue}
            onChange={setBenefitValue}
          />
        </Col>
      </Row>

      <div className="mt-4 p-3 border bg-light rounded">
        <strong>Resultado:</strong>
        <p className="mt-2 mb-0 text-muted">
          {servicosNecessarios > 0 ? (
            <>
              O cliente precisa realizar <strong>{servicosNecessarios}</strong> serviço(s) para resgatar{' '}
              <strong>{formatInReal(valorBeneficio)}</strong>.<br />
              Retorno médio por serviço: <strong>{formatInReal(retornoPorServico)}</strong>.
            </>
          ) : (
            <>Preencha todos os campos para ver a simulação.</>
          )}
        </p>
      </div>
    </>
  );
}

function SimulacaoProporcional({
  percentualPontos,
  pontosParaResgatar,
  valorBeneficio,
  setPercentualPontos,
  setPontosParaResgatar,
  setValorBeneficio,
  parseNum,
}) {
  const percentual = parseNum(percentualPontos);
  const pontosNecessarios = parseNum(pontosParaResgatar);
  const valorPremio = parseNum(valorBeneficio);

  // percentual = pontos sobre 100 reais? Aqui interpretamos como "pontos por 100 reais" em %
  // Ex.: 1% → 1 ponto por R$1 (ou 100 pts por R$100)
  // Vamos manter sua lógica: pontosPorReal = percentual/100.
  const pontosPorReal = useMemo(() => (percentual > 0 ? percentual / 100 : 0), [percentual]);
  const valorNecessario = useMemo(
    () => (pontosPorReal > 0 ? pontosNecessarios / pontosPorReal : 0),
    [pontosNecessarios, pontosPorReal]
  );
  const retornoPct = useMemo(
    () => (valorNecessario > 0 ? (valorPremio / valorNecessario) * 100 : 0),
    [valorPremio, valorNecessario]
  );

  return (
    <>
      <p className="mb-3">Simulação C: pontos proporcionais ao valor gasto.</p>

      <InfoAccordion title="Dicas para configurar (Simulação C)">
        <ul className="mb-2">
          <li>Defina uma proporção simples (ex.: 1 ponto por R$1,00 gasto).</li>
          <li>Busque retorno entre 5% e 10% para manter sustentabilidade.</li>
          <li>Perfeito para campanhas simples e automáticas.</li>
        </ul>
        <div className="border rounded p-3 bg-light">
          <strong>Exemplo:</strong><br />
          - 1 ponto por R$1,00<br />
          - Resgate: 1000 pontos = R$70 → gastar R$1000 e ganhar R$70 (retorno 7%)
        </div>
      </InfoAccordion>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <AppInput
            label="Percentual convertido em pontos (%)"
            name="percentualPontos"
            placeholder="Ex.: 100 = 1 pt por R$1"
            errors={[]}
            value={percentualPontos}
            onChange={setPercentualPontos}
          />
        </Col>
        <Col md={4}>
          <AppInput
            label="Pontos para resgate"
            name="pontosParaResgatar"
            placeholder="Ex.: 1000"
            errors={[]}
            value={pontosParaResgatar}
            onChange={setPontosParaResgatar}
          />
        </Col>
        <Col md={4}>
          <AppInput
            label="Valor do benefício (R$)"
            name="valorBeneficio"
            placeholder="Ex.: 70"
            errors={[]}
            value={valorBeneficio}
            onChange={setValorBeneficio}
          />
        </Col>
      </Row>

      <div className="mt-4 p-3 border bg-light rounded">
        <strong>Resultado:</strong>
        <p className="mt-2 mb-0 text-muted">
          {valorNecessario > 0 ? (
            <>
              O cliente precisa gastar <strong>{formatInReal(valorNecessario)}</strong> para resgatar{' '}
              <strong>{formatInReal(valorPremio)}</strong>.<br />
              Retorno estimado: <strong>{retornoPct.toFixed(1)}%</strong> (
              ~R${(valorPremio / (valorNecessario || 1)).toFixed(2)} de volta por R$1,00 gasto).
            </>
          ) : (
            <>Preencha todos os campos para ver a simulação.</>
          )}
        </p>
      </div>

    </>
  );
}